import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeft, User, Phone, Mail, Edit3, Calendar, Clock, MapPin,
  Trophy, Star, LogOut, Loader2, Check, X, Heart, ChevronRight,
  Bell, MessageSquare, Repeat, RefreshCw
} from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { FACILITY_IMAGES, SPORT_LABELS, DEFAULT_VENUE_IMAGE } from '../config/venueConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPORTS_OPTIONS = ['Football', 'Padel', 'Pickleball', 'Cricket'];

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout, updateProfile, getToken } = useCustomerAuth();

  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const [bookings, setBookings] = useState([]);
  const [communityGames, setCommunityGames] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingGames, setLoadingGames] = useState(true);

  const [bookingTab, setBookingTab] = useState('upcoming');
  const [gameTab, setGameTab] = useState('upcoming');

  // Favourites & notification prefs
  const [facilities, setFacilities] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState({
    sms_enabled: true, whatsapp_enabled: true, push_enabled: true,
    booking_reminders: true, game_updates: true
  });
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      fetchCommunityGames();
      loadFacilities();
      if (user?.notification_preferences) {
        setNotifPrefs(user.notification_preferences);
      }
    }
  }, [isAuthenticated]);

  const loadFacilities = async () => {
    try {
      const data = await api.getPublicFacilities();
      setFacilities(data);
    } catch { /* silent */ }
  };

  const toggleFavourite = async (venueId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/public/auth/toggle-favorite?venue_id=${venueId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Update local user state via refetch
        toast({ title: data.action === 'added' ? 'Added to favourites' : 'Removed from favourites' });
        window.location.reload();
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const saveNotifPrefs = async (key, value) => {
    const updated = { ...notifPrefs, [key]: value };
    setNotifPrefs(updated);
    try {
      const token = getToken();
      await fetch(`${API_URL}/api/public/auth/notification-preferences`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
    } catch { /* silent */ }
  };

  const fetchBookings = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/public/auth/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
      }
    } catch {
      // silent
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchCommunityGames = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/public/auth/community-games`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCommunityGames(Array.isArray(data) ? data : []);
      }
    } catch {
      // silent
    } finally {
      setLoadingGames(false);
    }
  };

  const startEditing = () => {
    setEditData({
      name: user?.name || '',
      email: user?.email || '',
      gender: user?.gender || '',
      sports: user?.sports || [],
      age: user?.age || ''
    });
    setEditing(true);
  };

  const handleSave = async () => {
    if (!editData.name?.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await updateProfile({
        name: editData.name.trim(),
        email: editData.email?.trim() || '',
        gender: editData.gender || '',
        sports: editData.sports || [],
        age: editData.age ? parseInt(editData.age) : null
      });
      setEditing(false);
      toast({ title: 'Saved', description: 'Profile updated' });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const today = new Date().toISOString().split('T')[0];

  const filteredBookings = bookings.filter(b => {
    if (bookingTab === 'upcoming') return b.date >= today && b.status !== 'cancelled';
    return b.date < today || b.status === 'cancelled' || b.status === 'completed';
  }).sort((a, b) => bookingTab === 'upcoming'
    ? new Date(a.date) - new Date(b.date)
    : new Date(b.date) - new Date(a.date)
  );

  const filteredGames = communityGames.filter(g => {
    const isCancelledByUser = g.my_payment_status === 'refunded' || g.my_payment_status === 'cancelled';
    if (gameTab === 'upcoming') return g.date >= today && g.status !== 'cancelled' && !isCancelledByUser;
    return g.date < today || g.status === 'cancelled' || g.status === 'completed' || isCancelledByUser;
  }).sort((a, b) => gameTab === 'upcoming'
    ? new Date(a.date) - new Date(b.date)
    : new Date(b.date) - new Date(a.date)
  );

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d + 'T00:00:00');
    const t = new Date();
    const tom = new Date(t); tom.setDate(t.getDate() + 1);
    if (d === t.toISOString().split('T')[0]) return 'Today';
    if (d === tom.toISOString().split('T')[0]) return 'Tomorrow';
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const p = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${p}`;
  };

  const toggleSport = (sport) => {
    setEditData(prev => ({
      ...prev,
      sports: prev.sports.includes(sport)
        ? prev.sports.filter(s => s !== sport)
        : [...prev.sports, sport]
    }));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A2C93]" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-[#0A2C93]" data-testid="profile-back">
            <ChevronLeft className="w-5 h-5" />
            <span className="font-heading font-semibold text-sm">Home</span>
          </button>
          <h1 className="font-heading font-bold text-[#0A2C93]">Profile</h1>
          <button onClick={handleLogout} className="text-sm text-red-500 font-medium" data-testid="profile-logout">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-6 space-y-6">
        {/* Section 1: Personal Info */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="profile-info-section">
          <div className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[#0A2C93] flex items-center justify-center">
                  <span className="text-xl font-bold text-white">
                    {(user.name || user.phone || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900" data-testid="profile-name">{user.name || 'Complete your profile'}</h2>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    +91 {user.phone}
                  </p>
                </div>
              </div>
              {!editing && (
                <button onClick={startEditing} className="p-2 rounded-lg hover:bg-slate-100 transition" data-testid="edit-profile-btn">
                  <Edit3 className="w-4 h-4 text-slate-500" />
                </button>
              )}
            </div>

            {editing ? (
              <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="edit-form">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
                    data-testid="edit-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Gender</label>
                  <div className="flex gap-2">
                    {['male', 'female', 'other'].map(g => (
                      <button
                        key={g}
                        onClick={() => setEditData(p => ({ ...p, gender: g }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition capitalize ${
                          editData.gender === g
                            ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]'
                            : 'border-slate-200 text-slate-600'
                        }`}
                      >{g}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Sports</label>
                  <div className="flex flex-wrap gap-2">
                    {SPORTS_OPTIONS.map(s => (
                      <button
                        key={s}
                        onClick={() => toggleSport(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                          editData.sports.includes(s)
                            ? 'border-[#0A2C93] bg-[#0A2C93]/5 text-[#0A2C93]'
                            : 'border-slate-200 text-slate-600'
                        }`}
                      >{s}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Email</label>
                  <input
                    type="email"
                    value={editData.email}
                    onChange={e => setEditData(p => ({ ...p, email: e.target.value }))}
                    placeholder="example@gmail.com"
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
                    data-testid="edit-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Age</label>
                  <input
                    type="number"
                    value={editData.age}
                    onChange={e => setEditData(p => ({ ...p, age: e.target.value }))}
                    placeholder="25"
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2C93]"
                    data-testid="edit-age"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 py-2.5 rounded-lg bg-[#0A2C93] text-white font-medium text-sm hover:bg-[#082273] disabled:bg-slate-300 transition flex items-center justify-center gap-1"
                    data-testid="save-profile-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Save
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-medium text-sm hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 pt-2 border-t border-slate-100 text-sm">
                {user.gender && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-500">Gender</span>
                    <span className="font-medium text-slate-900 capitalize">{user.gender}</span>
                  </div>
                )}
                {user.sports?.length > 0 && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-500">Sports</span>
                    <div className="flex gap-1.5 flex-wrap justify-end">
                      {user.sports.map(s => (
                        <span key={s} className="px-2 py-0.5 bg-[#0A2C93]/5 text-[#0A2C93] rounded-full text-xs font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {user.email && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-500">Email</span>
                    <span className="font-medium text-slate-900">{user.email}</span>
                  </div>
                )}
                {user.age && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-slate-500">Age</span>
                    <span className="font-medium text-slate-900">{user.age}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Bookings */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="bookings-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#0A2C93]" />
              My Bookings
            </h3>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {['upcoming', 'past'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setBookingTab(tab)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition capitalize ${
                    bookingTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                  data-testid={`booking-tab-${tab}`}
                >{tab}</button>
              ))}
            </div>
          </div>
          <div className="p-4">
            {loadingBookings ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No {bookingTab} bookings
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBookings.slice(0, 5).map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`booking-${b.id}`}>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{b.facility_name || b.court_id || 'Venue'}</p>
                      <p className="text-xs text-slate-500">{formatDate(b.date)} | {formatTime(b.start_time)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {bookingTab === 'past' && b.status !== 'cancelled' && b.facility_id && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/book/${b.facility_id}`); }}
                          className="flex items-center gap-1 px-2 py-1 rounded-lg bg-[#0A2C93]/5 text-[10px] font-semibold text-[#0A2C93] hover:bg-[#0A2C93]/10 transition"
                          data-testid={`rebook-${b.id}`}
                        >
                          <Repeat className="w-3 h-3" /> Rebook
                        </button>
                      )}
                      <div className="text-right">
                        <p className="font-semibold text-sm text-slate-900">₹{b.amount || 0}</p>
                      <p className={`text-xs font-medium capitalize ${
                        b.payment_status === 'paid' ? 'text-[#0A2C93]' :
                        b.status === 'cancelled' ? 'text-red-500' : 'text-amber-600'
                      }`}>
                        {b.status === 'cancelled' ? 'Cancelled' : b.payment_status || 'Pending'}
                      </p>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredBookings.length > 5 && (
                  <button
                    onClick={() => navigate('/my-bookings')}
                    className="w-full text-center text-sm text-[#0A2C93] font-medium py-2 hover:bg-[#0A2C93]/5 rounded-lg transition"
                  >
                    View all bookings →
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Community Games */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="games-section">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" />
              Community Games
            </h3>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {['upcoming', 'past'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setGameTab(tab)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition capitalize ${
                    gameTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                  }`}
                  data-testid={`game-tab-${tab}`}
                >{tab}</button>
              ))}
            </div>
          </div>
          <div className="p-4">
            {loadingGames ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
            ) : filteredGames.length === 0 ? (
              <div className="text-center py-8 text-sm text-slate-400">
                No {gameTab} games
              </div>
            ) : (
              <div className="space-y-3">
                {filteredGames.slice(0, 5).map(g => (
                  <div key={g.id} onClick={() => navigate(`/community/${g.venue_slug || g.venue_id}/game/${g.id}`)}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition" data-testid={`game-${g.id}`}>
                    <div>
                      <p className="font-medium text-sm text-slate-900 capitalize">{g.sport}{g.format ? ` ${g.format}` : ''}</p>
                      <p className="text-xs text-slate-500">{formatDate(g.date)} | {formatTime(g.start_time)}</p>
                      <p className="text-xs text-slate-400">{g.venue_name}</p>
                      {(g.my_payment_status === 'refunded' && g.my_amount_refunded > 0) && (
                        <p className="text-xs text-emerald-600 font-medium mt-1" data-testid={`refund-info-${g.id}`}>
                          Refunded ₹{g.my_amount_refunded.toFixed(0)} ({g.my_refund_percentage}%)
                        </p>
                      )}
                      {(g.my_payment_status === 'refunded' && g.my_amount_refunded === 0) && (
                        <p className="text-xs text-red-500 font-medium mt-1">No refund (within 3 hrs)</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-sm text-slate-900">₹{g.cost_per_player || 0}</p>
                      <p className={`text-xs font-medium capitalize ${
                        g.status === 'cancelled' ? 'text-red-500' :
                        g.my_payment_status === 'refunded' ? 'text-orange-500' : 'text-[#0A2C93]'
                      }`}>
                        {g.my_payment_status === 'refunded' ? 'Cancelled' :
                         g.status === 'cancelled' ? 'Cancelled' : g.status || 'Active'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 4: Favourite Venues */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="favourites-section">
          <div className="p-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-400" />
              Favourite Venues
            </h3>
          </div>
          <div className="p-4">
            {(user?.favorite_venues || []).length === 0 ? (
              <div className="text-center py-6 text-sm text-slate-400">
                <p>No favourite venues yet</p>
                <button onClick={() => navigate('/explore')} className="text-[#0A2C93] font-medium mt-1">Explore venues</button>
              </div>
            ) : (
              <div className="flex overflow-x-auto gap-3 pb-1 scrollbar-hide">
                {facilities.filter(f => (user?.favorite_venues || []).includes(f.id)).map(fav => (
                  <button
                    key={fav.id}
                    onClick={() => navigate(`/venue/${fav.slug || fav.id}`)}
                    className="flex-shrink-0 w-[150px] rounded-xl border border-slate-100 overflow-hidden text-left hover:-translate-y-0.5 transition-all"
                    data-testid={`fav-venue-${fav.id}`}
                  >
                    <img src={FACILITY_IMAGES[fav.id] || DEFAULT_VENUE_IMAGE} alt={fav.name} className="w-full h-20 object-cover" />
                    <div className="p-2 flex items-center justify-between">
                      <span className="font-heading font-bold text-xs text-[#0A2C93] truncate">{fav.name}</span>
                      <Heart className="w-3 h-3 text-red-400 fill-red-400 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 5: Stats */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stats-section">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Star className="w-4 h-4 text-purple-500" />
            Stats
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-slate-900">{user.total_bookings || bookings.length || 0}</p>
              <p className="text-xs text-slate-500">Bookings</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{user.total_games_played || communityGames.length || 0}</p>
              <p className="text-xs text-slate-500">Games</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 capitalize">
                {user.sports?.[0] || '-'}
              </p>
              <p className="text-xs text-slate-500">Fav Sport</p>
            </div>
          </div>
        </div>

        {/* Section 6: Notification Preferences - Coming Soon */}
        {/* Notification toggles temporarily hidden
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="notification-prefs-section">
          ...
        </div>
        */}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <Link to="/" className="flex items-center justify-between p-4 hover:bg-slate-50 transition border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#0A2C93]/5 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-[#0A2C93]" />
              </div>
              <span className="text-sm font-medium text-slate-900">Book a Slot</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </Link>
          <button onClick={handleLogout} className="flex items-center justify-between p-4 hover:bg-slate-50 transition w-full" data-testid="logout-action">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
                <LogOut className="w-4 h-4 text-red-500" />
              </div>
              <span className="text-sm font-medium text-red-600">Logout</span>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
